import {
  CombiDealSlotModel,
  ExpirationType,
  ProductAllergenFlagsModel,
  ProductDefinitionModel,
  ProductPointOfSaleModel,
} from '../dto';
import { MaccsBoxTicketType } from '../enums/MaccsBoxTicketType';
import { ProductType } from '../enums/ProductType';
import { SubscriptionModel } from '../models/subscription.model';
import { Media } from './Media';
import { ProductAdditionalCost } from './ProductAdditionalCost';
import { ProductDescriptor } from './ProductDescriptor';
import { ProductGroup } from './ProductGroup';
import { ProductPrice } from './ProductPrice';
import { ProductVariant } from './ProductVariant';
import { Shop } from './Shop';
import { Supplement } from './Supplement';

export interface Product {
  id: string;
  company_id: string;
  name?: string;
  public_name: string;
  private_name: string;
  is_active: boolean;
  original_price: number;
  scanner_info: string;
  subtitle: string;
  description: string;
  url: string;
  maker: string;
  tags: string;
  label: string;
  type: ProductType;
  available_from: string;
  available_to: string;
  sku: string;
  order: number | null;
  barcode: string;
  instances_infinite: boolean;
  instances_allow_exceed_amount: boolean;
  instances_available: number;
  instances_amount: number;
  instances_amount_difference: number;
  decrement_stock_on_order: boolean;
  instances: ProductInstance;
  max_quantity_per_order: number;
  current_price: ProductPrice;
  media_ids: string[];
  media: Media[];
  prices: ProductPrice[];
  shop_ids: string[];
  points_of_sale_ids: string[];
  points_of_sale_as_addon_ids: string[];
  points_of_sale: { pos_id: string; type: ProductPointOfSaleModel[] }[];
  shops: Shop[];
  variants: ProductVariant[];
  variant_choice_label: string;
  combi_slots: CombiDealSlotModel[] | null;
  combi_base_price: number | null;
  allergens: ProductAllergenFlagsModel | null;
  alcohol_percentage: number | null;
  /** flixit only (flixit-be); the Pauly API no longer returns it (PAUL-3045). */
  additional_costs: ProductAdditionalCost[];
  product_pricing_tag_ids: string[];
  descriptors: ProductDescriptor[];
  regular_amount?: number; // Only for front usage
  custom_label: string; // Only for front usage
  group_ids: string[];
  groups: ProductGroup[];
  highlighted_to: string;
  highlighted_to_error: false;
  liked: boolean;
  voucher_description: string;
  voucher_available_to: string;
  voucher_photo_id: string;
  voucher_photo: string;
  voucher_generate_codes: boolean;
  /** PAUL-3045: derived by the API from the current price row (transition, until 2027-01-02). Read the rows. */
  general_ledger_account_id: string;
  upsell_ids: string[];
  subscription: SubscriptionModel;
  /** PAUL-3794: on a subscription ticket, the subscriptions that accept it - `subscription.id`, not product ids. */
  subscription_ids?: string[] | null;
  import_feed_entry_id: string;
  voucher_single_code: string;
  voucher_discount: string;
  color: string;
  seat_type_ids: string[];
  /** flixit only (flixit-be); the Pauly API no longer returns it (PAUL-3045). */
  show_additional_costs: boolean;
  /**
   * Price this product against the show it is sold for rather than the moment of ordering
   * (PAUL-3533). Opt-in; a line with no show falls back to the order date whatever it says.
   */
  price_follows_show_date: boolean;
  supplement_ids: string[] | null;
  supplement_products: Supplement[] | null;
  activation_ready: boolean;
  restaurant_definition: ProductDefinitionModel;
  status: ProductStatus;
  display_status?: ProductDisplayStatus;
  is_required?: boolean;
  show_most_frequent_values: boolean;
  most_frequent_values: ProductFrequentValueModel[] | null;
  has_expiration_date: boolean;
  expiration_unit: ExpirationType;
  expires_in: number | null;
  // PAUL-3008: the company's default gift-card product (at most one). Only meaningful on gift-card
  // products; a shop without one cannot issue gift-card refunds.
  is_default: boolean;
  isSelected?: boolean; // Only for front usage
  display_step: ProductDisplayStep;
  is_mutually_exclusive: boolean;
  is_limited_to_main_product_quantity: boolean;
  is_cancellation_fee: boolean;
  is_free_ticket: boolean;
  required_payment_method_id: string;
  is_cineville_ticket: boolean;
  is_subscription_ticket: boolean;
  counts_toward_box_office: boolean;
  show_format_surcharge: boolean;
  custom_value_supported: boolean;
  box_office_rules?: ProductBoxOfficeRuleModel[];
}

export interface ProductBoxOfficeRuleModel {
  id?: string;
  product_id?: string;
  available_from?: string;
  amount?: number;
  /**
   * PAUL-3449: Maccsbox TicketTypeCd; only Normal/Discount/Other are accepted. Optional only for reading:
   * rules saved before the column existed come back without it and the form falls back to Normal. Always
   * send it when saving - the backend stores Normal for an omitted value, also on edit.
   */
  ticket_type?: MaccsBoxTicketType;
  /**
   * PAUL-3803: the VAT Maccsbox reports this amount with, instead of the ticket price's VAT. Null follows the
   * price. The PUT replaces every rule, so send it on each one - omitting it clears the override.
   */
  override_tax_id?: string | null;
  /** Read-only. */
  override_tax_name?: string | null;
  /** Read-only, a fraction (0.09). */
  override_tax_rate?: number | null;
}

export interface ProductFrequentValueModel {
  id?: string;
  value: number;
  order: number;
}

export enum ProductStatus {
  Draft = 0,
  Active = 1,
}

/**
 * Tri-state product status shown in the dashboard, derived server-side (PAUL-2834).
 * Combines the persisted publish status (Draft/Active) with the availability window:
 * an Active product that is currently outside its availability window reads as Inactive.
 * Mirrors the backend `ProductDisplayStatus` enum — sent as the `statuses` list filter and
 * returned on the product as `display_status`.
 */
export enum ProductDisplayStatus {
  Draft = 0,
  Active = 1,
  Inactive = 2,
}

export const productDisplayStatusTranslations: Record<ProductDisplayStatus, string> = {
  [ProductDisplayStatus.Draft]: 'product.draft',
  [ProductDisplayStatus.Active]: 'product.active',
  [ProductDisplayStatus.Inactive]: 'product.inactive',
};

export const productDisplayStatusOptions: { text: string; val: ProductDisplayStatus }[] = [
  { text: productDisplayStatusTranslations[ProductDisplayStatus.Active], val: ProductDisplayStatus.Active },
  { text: productDisplayStatusTranslations[ProductDisplayStatus.Inactive], val: ProductDisplayStatus.Inactive },
  { text: productDisplayStatusTranslations[ProductDisplayStatus.Draft], val: ProductDisplayStatus.Draft },
];

export enum ProductInstance {
  Unknown = 0,
  Available = 1,
  Unavailable = 2,
}

export enum ProductDisplayStep {
  None = 0,
  ProductsPage = 1,
  SupplementsPage = 2,
}

/** Where the webshop offers a supplement; keys in the common translations, so every app has them. */
export const ProductDisplayStepTranslations = {
  [ProductDisplayStep.None]: 'product_display_step.none',
  [ProductDisplayStep.ProductsPage]: 'product_display_step.products_page',
  [ProductDisplayStep.SupplementsPage]: 'product_display_step.supplements_page',
};

export const ProductDisplayStepOptions = [
  { key: ProductDisplayStepTranslations[ProductDisplayStep.None], value: ProductDisplayStep.None },
  { key: ProductDisplayStepTranslations[ProductDisplayStep.ProductsPage], value: ProductDisplayStep.ProductsPage },
  {
    key: ProductDisplayStepTranslations[ProductDisplayStep.SupplementsPage],
    value: ProductDisplayStep.SupplementsPage,
  },
];
